import {
  IJournalPublishingModel,
  ISubmissionContext,
  journalPublishingModels
} from '@publishing-engine/domain/models';
import { IJournalSettingFormData } from '..';

export const JOURNAL_DESCRIPTION_MAX_LENGTH = 2500;

export type GeneralInfoFieldInputType = 'text' | 'textarea' | 'radio';

export type GeneralInfoFieldKey =
  | 'name'
  | 'shortname'
  | 'description'
  | 'introduction'
  | 'url'
  | 'issnPrint'
  | 'issnOnline'
  | 'publishingModel';

/**
 * Fields that reuse this edit affordance from another tab. They are kept out of
 * `GeneralInfoFieldKey` so `GENERAL_INFO_EDITABLE_FIELD_MAP` can stay a total
 * record of the journal-level fields the General tab renders.
 */
export type GeneralInfoEditableFieldKey =
  | GeneralInfoFieldKey
  | 'peerReviewModel';

export type GeneralInfoEditFormValues = {
  value: string;
};

export type GeneralInfoRadioOption = {
  value: string;
  label: string;
  subText: string;
};

export type GeneralInfoEditableFieldConfig = {
  key: GeneralInfoEditableFieldKey;
  label: string;
  modalTitle: string;
  inputType: GeneralInfoFieldInputType;
  placeholder?: string;
  isRequired?: boolean;
  maxLength?: number;
  subText?: string;
  options?: GeneralInfoRadioOption[];
  isEditable?: boolean;
  /**
   * Explains the disabled edit icon on hover. Only read when `isEditable` is
   * false, so a locked field stays discoverable instead of losing its icon.
   */
  disabledTooltip?: string;
  getValue: (journal: ISubmissionContext) => string;
  buildPayload: (
    value: string,
    journal: ISubmissionContext
  ) => IJournalSettingFormData;
  validate?: (value: string, journal: ISubmissionContext) => true | string;
};

export const getDisplayValue = (value?: string | null) =>
  value && value.trim().length > 0 ? value : 'No data available';

export const getPublishingModelDisplayValue = (journal: ISubmissionContext) =>
  journal.config?.publishingModel
    ? journalPublishingModels[journal.config.publishingModel]
    : 'No data available';

export const validateWhitespace = (
  value: string,
  label: string,
  isRequired: boolean
): true | string => {
  if (!value.length) {
    return isRequired ? `${label} is required` : true;
  }

  if (!value.trim().length) {
    return `${label} cannot contain only whitespace`;
  }

  return true;
};

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const validateEmailValue = (value: string): true | string => {
  const trimmedValue = value.trim();

  if (!EMAIL_REGEX.test(trimmedValue)) {
    return 'Please enter a valid email address';
  }

  return true;
};

export const validateJournalUrlValue = (value: string): true | string => {
  const trimmedValue = value.trim();

  if (/\s/.test(trimmedValue)) {
    return 'Journal URL cannot contain spaces';
  }

  if (!/^https?:\/\//i.test(trimmedValue)) {
    return 'Journal URL must start with http:// or https://';
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(trimmedValue);
  } catch {
    return 'Please enter a valid journal URL';
  }

  if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
    return 'Please enter a valid journal URL';
  }

  const hostname = parsedUrl.hostname;
  if (!hostname || hostname.startsWith('.') || hostname.endsWith('.')) {
    return 'Journal URL must include a valid domain name';
  }

  if (hostname.includes('..')) {
    return 'Journal URL must include a valid domain name';
  }

  const domainLabels = hostname.split('.');
  if (
    domainLabels.length < 2 ||
    domainLabels.some(label => label.trim().length === 0)
  ) {
    return 'Journal URL must include a valid domain name';
  }

  return true;
};

const ALPHANUMERIC_REGEX = /^[A-Za-z0-9]+$/;

export const validateAlphaNumericValue = (value: string): true | string => {
  const trimmedValue = value.trim();

  if (!ALPHANUMERIC_REGEX.test(trimmedValue)) {
    return 'Only letters and numbers are allowed';
  }

  return true;
};

const ISSN_REGEX = /^[0-9]{4}-[0-9]{3}[0-9Xx]$/;

export const validateIssnValue = (value: string): true | string => {
  const trimmedValue = value.trim();

  if (!ISSN_REGEX.test(trimmedValue)) {
    return 'Enter a valid ISSN in the format 1234-5678';
  }

  return true;
};

export const validateIssnNotSameAs = (
  value: string,
  otherIssn: string | null | undefined
): true | string => {
  const trimmedValue = value.trim().toLowerCase();
  const trimmedOther = otherIssn?.trim().toLowerCase();

  if (trimmedValue && trimmedOther && trimmedValue === trimmedOther) {
    return 'ISSN online and print cannot be the same';
  }

  return true;
};

const getPublishingModelValue = (journal: ISubmissionContext) =>
  journal.config?.publishingModel ?? 'openAccess';

const buildDataPayload = (
  key: 'name' | 'shortname' | 'description' | 'introduction',
  value: string
): IJournalSettingFormData => ({
  data: {
    [key]: value
  }
});

const buildConfigPayload = (
  key: 'url' | 'email' | 'publishingModel' | 'issn',
  value:
    | string
    | {
        print?: string;
        online?: string;
      }
): IJournalSettingFormData => ({
  config: {
    [key]: value
  }
});

const PUBLISHING_MODEL_SUBTEXTS: Record<IJournalPublishingModel, string> = {
  openAccess: 'Research information will be available to readers at no cost.',
  subscription:
    'Readers only have access to research by paying a subscription.',
  hybrid: 'Choose between subscription and open access for each article.'
};

/**
 * Builds radio options from a domain label record so the models in
 * domain/models/submission-context.ts stay the single source of truth for
 * option values and labels. Only the sub-text is presentation, kept here —
 * keying it by the model union makes a missing model a compile error.
 */
export const buildRadioOptions = <T extends string>(
  modelLabels: Record<T, string>,
  modelSubTexts: Record<T, string>
): GeneralInfoRadioOption[] =>
  (Object.keys(modelSubTexts) as T[]).map(value => ({
    value,
    label: modelLabels[value],
    subText: modelSubTexts[value]
  }));

/**
 * Journal Name and Journal Code are frozen once a journal goes live. That state
 * arrives per-render, so it is layered onto the static config rather than baked
 * into the field list above. Publishing Model is not listed here — it is frozen
 * outright via its own `isEditable: false`, which this helper would otherwise
 * overwrite.
 */
export const withJournalActiveLock = (
  field: GeneralInfoEditableFieldConfig,
  isActive: boolean | undefined
): GeneralInfoEditableFieldConfig => ({
  ...field,
  isEditable: !isActive
});

export const GENERAL_INFO_EDITABLE_FIELDS = [
  {
    key: 'name',
    label: 'Journal Name',
    modalTitle: 'Edit Journal Name',
    inputType: 'text',
    placeholder: 'Enter journal Name',
    isRequired: true,
    disabledTooltip:
      'Journal Name can only be edited when the journal is inactive.',
    getValue: journal => journal.data?.name ?? '',
    buildPayload: value => buildDataPayload('name', value)
  },
  {
    key: 'shortname',
    label: 'Journal Code',
    modalTitle: 'Edit Journal Code',
    inputType: 'text',
    placeholder: 'Enter journal code',
    isRequired: true,
    disabledTooltip:
      'Journal Code can only be edited when the journal is inactive.',
    getValue: journal => journal.data?.shortname ?? '',
    buildPayload: value => buildDataPayload('shortname', value),
    validate: validateAlphaNumericValue
  },
  {
    key: 'description',
    label: 'Journal Description',
    modalTitle: 'Edit Journal Description',
    inputType: 'textarea',
    placeholder: 'Enter journal description',
    isRequired: true,
    maxLength: JOURNAL_DESCRIPTION_MAX_LENGTH,
    getValue: journal => journal.data?.description ?? '',
    buildPayload: value => buildDataPayload('description', value)
  },
  {
    key: 'introduction',
    label: 'Journal Introduction',
    modalTitle: 'Edit Journal Introduction',
    inputType: 'textarea',
    placeholder: 'Enter journal introduction',
    isRequired: true,
    maxLength: JOURNAL_DESCRIPTION_MAX_LENGTH,
    getValue: journal => journal.data?.introduction ?? '',
    buildPayload: value => buildDataPayload('introduction', value)
  },
  {
    key: 'url',
    label: 'Journal URL',
    modalTitle: 'Edit Journal URL',
    inputType: 'text',
    placeholder: 'Enter journal URL',
    isRequired: true,
    subText:
      "Please enter a link to this Journal's home page so that authors can gather information about the Journal and its focus points.",
    getValue: journal => journal.config?.url ?? '',
    buildPayload: value => buildConfigPayload('url', value),
    validate: validateJournalUrlValue
  },
  {
    key: 'issnPrint',
    label: 'ISSN (Print)',
    modalTitle: 'Edit ISSN (Print)',
    inputType: 'text',
    placeholder: 'Enter ISSN for print version (e.g., 2046-3758)',
    isRequired: false,
    subText: 'International Standard Serial Number for print version',
    getValue: journal => journal.config?.issn?.print ?? '',
    buildPayload: (value, journal) =>
      buildConfigPayload('issn', {
        print: value,
        online: journal.config?.issn?.online ?? ''
      }),
    validate: (value, journal) => {
      const formatValidation = validateIssnValue(value);
      if (formatValidation !== true) {
        return formatValidation;
      }

      return validateIssnNotSameAs(value, journal.config?.issn?.online);
    }
  },
  {
    key: 'issnOnline',
    label: 'ISSN (Online)',
    modalTitle: 'Edit ISSN (Online)',
    inputType: 'text',
    placeholder: 'Enter ISSN for online version (e.g., 2046-3758)',
    isRequired: false,
    subText: 'International Standard Serial Number for online version',
    getValue: journal => journal.config?.issn?.online ?? '',
    buildPayload: (value, journal) =>
      buildConfigPayload('issn', {
        print: journal.config?.issn?.print ?? '',
        online: value
      }),
    validate: (value, journal) => {
      const formatValidation = validateIssnValue(value);
      if (formatValidation !== true) {
        return formatValidation;
      }

      return validateIssnNotSameAs(value, journal.config?.issn?.print);
    }
  },
  {
    key: 'publishingModel',
    label: 'Publishing Model',
    modalTitle: 'Edit Publishing Model',
    inputType: 'radio',
    isRequired: true,
    isEditable: false, // Publishing Model is frozen for now, so no edit allowed
    disabledTooltip:
      'Publishing Model is frozen for now until get the proper requirements.',
    getValue: getPublishingModelValue,
    buildPayload: value => buildConfigPayload('publishingModel', value),
    options: buildRadioOptions(
      journalPublishingModels,
      PUBLISHING_MODEL_SUBTEXTS
    )
  }
] as const satisfies ReadonlyArray<GeneralInfoEditableFieldConfig>;

/**
 * The same fields, keyed by `key`. Built by hand rather than through
 * `Object.fromEntries` so the keys are checked against `GeneralInfoFieldKey`
 * instead of being cast to it.
 */
export const GENERAL_INFO_EDITABLE_FIELD_MAP =
  GENERAL_INFO_EDITABLE_FIELDS.reduce((fieldsByKey, field) => {
    fieldsByKey[field.key] = field;
    return fieldsByKey;
  }, {} as Record<GeneralInfoFieldKey, GeneralInfoEditableFieldConfig>);
